import { useState, useEffect, createContext, useContext, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);

  const logout = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_tokens');
  }, []);

  useEffect(() => {
    const checkAuthTokens = () => {
      const storedTokens = localStorage.getItem('auth_tokens');
      if (storedTokens) {
        try {
          const { access_token, refresh_token, user_data } = JSON.parse(storedTokens);
          setAccessToken(access_token);
          setRefreshToken(refresh_token);
          setUser(user_data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Error parsing stored auth tokens:', error);
          localStorage.removeItem('auth_tokens');
          setAccessToken(null);
          setRefreshToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setAccessToken(null);
        setRefreshToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuthTokens();

    const handleLogout = () => {
      logout();
    };

    const handleTokenRefreshed = (e) => {
      const storedTokens = localStorage.getItem('auth_tokens');
      if (storedTokens) {
        try {
          const { access_token } = JSON.parse(storedTokens);
          setAccessToken(access_token);
        } catch (error) {
          console.error('Error parsing stored auth tokens:', error);
        }
      }
    };

    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);
    window.addEventListener('storage', (e) => {
      if (e.key === 'auth_tokens' || e.key === null) {
        checkAuthTokens();
      }
    });

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
    };
  }, [logout]);

  const login = (userData, tokens) => {
    setUser(userData);
    setAccessToken(tokens.access_token);
    setRefreshToken(tokens.refresh_token);
    setIsAuthenticated(true);
    
    localStorage.setItem('auth_tokens', JSON.stringify({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      user_data: userData
    }));
  };

  const refreshAccessToken = async () => {
    if (!refreshToken) return false;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.access_token);
        
        const storedTokens = JSON.parse(localStorage.getItem('auth_tokens'));
        storedTokens.access_token = data.access_token;
        localStorage.setItem('auth_tokens', JSON.stringify(storedTokens));
        
        return true;
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
    }
    
    logout(); // Se não conseguir renovar, faz logout
    return false;
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    accessToken,
    login,
    logout,
    refreshAccessToken,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 